import Client from "./client";

export const GetProjects = (accessToken) => {
    return new Promise((resolve, reject) => {
        Client.get('/api/projects', {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.projects);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const CreateProject = ({name, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/projects', {name}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.project);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const UpdateProject = ({projectId, name, code, allowAnonymousBugEntry, allowPublicSupport, allowSupportEmbed, supportEmbedOrigins, accessToken}) => {
    const data = {};
    if (name !== undefined) data.name = name;
    if (code !== undefined) data.code = code;
    if (allowAnonymousBugEntry !== undefined) data.allowAnonymousBugEntry = allowAnonymousBugEntry;
    if (allowPublicSupport !== undefined) data.allowPublicSupport = allowPublicSupport;
    if (allowSupportEmbed !== undefined) data.allowSupportEmbed = allowSupportEmbed;
    if (supportEmbedOrigins !== undefined) data.supportEmbedOrigins = supportEmbedOrigins;

    return new Promise((resolve, reject) => {
        Client.put(`/api/projects/${projectId}`, data, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            // `status` as well as the body: a 402 here means the project's plan
            // doesn't include the feature being switched on, which the caller
            // offers an upgrade for rather than reporting as a failure.
            reject({
                ...(e.response?.data || { error: "Something went wrong. Please try again." }),
                status: e.response?.status,
            });
        })
    })
}

export const GetProjectDetails = (id, accessToken) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${id}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// Same data GetProjectDetails already returns (project_code, current_sprint,
// sprints) - reuses GET /projects/:id rather than a second backend route for
// the identical query.
export const GetSprints = (id, accessToken) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${id}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        });
    })
};

export const CreateSprint = ({projectId, name, goal, startdate, enddate, phaseIds, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/sprints`, {name, goal, startdate, enddate, phaseIds}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.sprint);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const StartSprint = ({projectId, sprintId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/sprints/${sprintId}/start`, {}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const CompleteSprint = ({projectId, sprintId, rolloverIncomplete, targetSprintId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/sprints/${sprintId}/complete`, {
            rolloverIncomplete: !!rolloverIncomplete,
            ...(targetSprintId ? {targetSprintId} : {}),
        }, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// The caller's own effective permission set on this project (see
// api/lib/permissions.js) - fetched once per project load to gate nav/tabs.
export const GetProjectPermissions = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/permissions`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.permissions);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// What this project's plan allows and how much of it is used - so a screen can
// say "not on this plan" before anybody fills in a form, or "3 of 5" without
// counting rows itself. Resolves {plan, usage}. Advisory: the same limits are
// applied again by the API on every write.
export const GetProjectPlanLimits = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/plan-limits`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve({plan: res.data.plan, usage: res.data.usage || {}});
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const GetProjectMembersDetailed = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/members-detailed`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.members);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const GetPendingProjectMembers = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/pending-members`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.pending);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const ApproveProjectMember = ({projectId, userId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/pending-members/${userId}/approve`, {}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            // `status` as well as the body: a 402 here means the project is
            // already full, which the caller offers an upgrade for rather
            // than reporting as a plain failure (same convention as
            // UpdateProject above).
            reject({
                ...(e.response?.data || { error: "Something went wrong. Please try again." }),
                status: e.response?.status,
            });
        })
    })
}

// Org members already approved onto this project (see ApproveProjectMember
// above) - the counterpart to GetPendingProjectMembers for people who've
// cleared that queue. There's no memberId/role to manage here, only who
// they are and which organization they came through - RemoveOrgMember below
// is the only action that applies to one.
export const GetApprovedOrgMembers = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/org-members`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.members);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// Revokes one org member's access to this project - they're identified by
// user id rather than a memberId, since they have no ProjectMember row of
// their own (see RemoveProjectMember below, which does).
export const RemoveOrgMember = ({projectId, userId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete(`/api/projects/${projectId}/org-members/${userId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const UpdateProjectMemberRole = ({projectId, memberId, roleId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.put(`/api/projects/${projectId}/members/${memberId}/role`, {roleId}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const UpdateProjectMemberOverrides = ({projectId, memberId, overrides, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.put(`/api/projects/${projectId}/members/${memberId}/overrides`, {overrides}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const RemoveProjectMember = ({projectId, memberId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete(`/api/projects/${projectId}/members/${memberId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const RemoveProjectFromOrganization = ({projectId, organizationId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete(`/api/projects/${projectId}/organizations/${organizationId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const UpdateProjectJoinSettings = ({projectId, allowAutoJoin, accessToken}) => {
    const data = {};
    if (allowAutoJoin !== undefined) data.allowAutoJoin = allowAutoJoin;

    return new Promise((resolve, reject) => {
        Client.put(`/api/projects/${projectId}/join-settings`, data, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// The budget bar's prediction, the per-sprint working-day capacity behind
// it, and the raw velocity history (for the sprint velocity charts). One
// call rather than three - see GET /projects/:id/velocity.
export const GetVelocity = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/velocity`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// One sprint's burndown series, replayed server-side from its ticket
// event log - see GET /projects/:id/burndown. Carries the total plus the
// same series split by story type and by contributor, so the Burndown
// page's breakdown filter switches without another round trip.
export const GetBurndown = ({projectId, sprintIndex, accessToken}) => {
    return new Promise((resolve, reject) => {
        const query = sprintIndex !== undefined && sprintIndex !== null ? `?sprintIndex=${sprintIndex}` : "";
        Client.get(`/api/projects/${projectId}/burndown${query}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// A sprint at a glance - the counts both dashboards show, tallied where the
// rows are rather than out of whatever the store happens to hold (see GET
// /projects/:id/summary). `sprintId` is optional; without it the server
// answers for the project's current sprint.
export const GetProjectSummary = ({projectId, sprintId, accessToken}) => {
    return new Promise((resolve, reject) => {
        const query = sprintId !== undefined && sprintId !== null ? `?sprintId=${sprintId}` : "";
        Client.get(`/api/projects/${projectId}/summary${query}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}
